/**
 * Catálogo: la pantalla donde nace el menú y el inventario.
 *
 * El campo de búsqueda es también el campo del escáner. Una pistola de código
 * de barras teclea los dígitos y da Enter, así que ambos caminos terminan en
 * lo mismo. Si el código no existe, en vez de un "no encontrado" inútil se
 * ofrece darlo de alta ahí mismo: es la única forma de que un catálogo de mil
 * productos se llene trabajando en vez de en una jornada de captura que nunca
 * llega.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, API_BASE, ErrorApi } from '../lib/api';
import { alRecibir } from '../lib/socket';
import { dinero, cantidad, ETIQUETA_ESTADO, CLASE_ESTADO } from '../lib/formato';
import type { Categoria, Producto, ResumenInventario } from '../lib/tipos';
import { EditorProducto } from '../componentes/EditorProducto';

interface SucursalBasica {
  id: number;
  nombre: string;
  tipo: 'restaurante' | 'abarrotes';
}

interface Props {
  sucursalId: number;
  tipo: 'platillo' | 'abarrote';
  sucursales?: SucursalBasica[];
}

export function Catalogo({ sucursalId, tipo, sucursales = [] }: Props) {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [resumen, setResumen] = useState<ResumenInventario | null>(null);
  const [buscar, setBuscar] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editando, setEditando] = useState<Producto | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [codigoNuevo, setCodigoNuevo] = useState<string | undefined>();

  const campoBusqueda = useRef<HTMLInputElement>(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams({ sucursalId: String(sucursalId), tipo });
      if (buscar.trim()) params.set('buscar', buscar.trim());
      if (filtroEstado) params.set('estado', filtroEstado);

      const [lista, cats, res] = await Promise.all([
        api<Producto[]>(`/catalogo/productos?${params}`),
        api<Categoria[]>(`/catalogo/categorias?sucursalId=${sucursalId}`),
        api<ResumenInventario>(`/catalogo/inventario/resumen?sucursalId=${sucursalId}`),
      ]);
      setProductos(lista);
      setCategorias(cats);
      setResumen(res);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar el catálogo.');
    } finally {
      setCargando(false);
    }
  }, [sucursalId, tipo, buscar, filtroEstado]);

  useEffect(() => {
    // Espera a que dejen de teclear. Sin esto, cada letra es una consulta.
    const t = setTimeout(cargar, buscar ? 250 : 0);
    return () => clearTimeout(t);
  }, [cargar, buscar]);

  // El socket avisa; la recarga la hace HTTP. Si otra tablet marca algo como
  // agotado, esta lista se entera sola.
  useEffect(() => alRecibir('catalogo:cambio', () => void cargar()), [cargar]);
  useEffect(() => alRecibir('sincronizar', () => void cargar()), [cargar]);

  /**
   * Enter en el campo de búsqueda: es lo que manda una pistola de código de
   * barras al terminar de leer. Si hay un solo resultado, lo abre. Si no hay
   * ninguno y parece un código, ofrece darlo de alta.
   */
  async function alEnviarBusqueda(e: React.FormEvent) {
    e.preventDefault();
    const texto = buscar.trim();
    if (!texto) return;

    const pareceCodigo = /^\d{6,}$/.test(texto);
    if (!pareceCodigo) return;

    try {
      const p = await api<Producto>(
        `/catalogo/productos/codigo/${encodeURIComponent(texto)}?sucursalId=${sucursalId}`
      );
      abrirEditor(p);
      setBuscar('');
    } catch (err) {
      if (err instanceof ErrorApi && err.codigo === 'CODIGO_NO_REGISTRADO') {
        setCodigoNuevo(texto);
        setEditando(null);
        setAbierto(true);
        setBuscar('');
      } else {
        setError(err instanceof ErrorApi ? err.message : 'No se pudo buscar.');
      }
    }
  }

  function abrirEditor(p: Producto | null) {
    setEditando(p);
    setCodigoNuevo(undefined);
    setAbierto(true);
  }

  function cerrarEditor() {
    setAbierto(false);
    setEditando(null);
    setCodigoNuevo(undefined);
    campoBusqueda.current?.focus();
  }

  async function accionRapida(p: Producto, accion: 'agotado' | 'publicado' | 'archivado' | 'duplicar') {
    if (accion === 'archivado' && !confirm(`¿Quitar "${p.nombre}" del catálogo? No se borra, pero deja de aparecer aquí y en el menú.`)) {
      return;
    }
    try {
      if (accion === 'duplicar') {
        await api(`/catalogo/productos/${p.id}/duplicar?sucursalId=${sucursalId}`, { method: 'POST' });
      } else {
        await api(`/catalogo/productos/${p.id}/estado?sucursalId=${sucursalId}`, {
          method: 'PATCH',
          body: { estado: accion },
        });
      }
      await cargar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo aplicar el cambio.');
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 pb-24 pt-6">
      <header className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">
            {tipo === 'platillo' ? 'Menú' : 'Productos'}
          </h1>
          {resumen && (
            <p className="mt-1 text-sm text-salvia">
              <span className="num">{resumen.publicados}</span> a la venta
              {Number(resumen.borradores) > 0 && (
                <> · <span className="num">{resumen.borradores}</span> en borrador</>
              )}
              {Number(resumen.bajo_minimo) > 0 && (
                <>
                  {' '}
                  · <span className="num font-semibold text-miel-oscuro">{resumen.bajo_minimo}</span>{' '}
                  por agotarse
                </>
              )}
            </p>
          )}
        </div>
        <button className="btn-principal" onClick={() => abrirEditor(null)}>
          + Nuevo
        </button>
      </header>

      <form onSubmit={alEnviarBusqueda} className="mb-4 flex gap-2">
        <input
          ref={campoBusqueda}
          className="campo flex-1"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          placeholder={tipo === 'abarrote' ? 'Escanea o escribe el nombre' : 'Buscar platillo'}
          autoComplete="off"
        />
        <select
          className="campo w-auto"
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          aria-label="Filtrar por estado"
        >
          <option value="">Todos</option>
          <option value="publicado">A la venta</option>
          <option value="borrador">Borradores</option>
          <option value="agotado">Agotados</option>
        </select>
      </form>

      {error && (
        <p role="alert" className="mb-4 rounded-lg bg-barro-claro px-4 py-3 text-sm text-barro-oscuro">
          {error}
        </p>
      )}

      {cargando ? (
        <p className="py-16 text-center text-salvia">Cargando…</p>
      ) : productos.length === 0 ? (
        <div className="rounded-xl border border-dashed border-salvia/30 py-16 text-center">
          <p className="font-titulo text-lg font-semibold">
            {buscar ? 'Nada con esa búsqueda' : 'Todavía no hay nada aquí'}
          </p>
          <p className="mx-auto mt-2 max-w-xs text-sm text-salvia">
            {buscar
              ? 'Prueba con otro nombre, o da de alta el producto.'
              : 'Empieza con lo que más se vende. Con veinte productos ya se puede abrir.'}
          </p>
          <button className="btn-principal mt-6" onClick={() => abrirEditor(null)}>
            Dar de alta el primero
          </button>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {productos.map((p) => {
            const bajo = p.controla_stock === 1 && Number(p.stock) <= Number(p.stock_minimo);
            return (
              <li
                key={p.id}
                className="flex items-center gap-3 rounded-xl border border-salvia/15 bg-white p-3"
              >
                <button
                  onClick={() => !p.compartido && abrirEditor(p)}
                  disabled={p.compartido}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default"
                >
                  {p.imagen_url ? (
                    <img
                      src={p.imagen_url.startsWith('http') || p.imagen_url.startsWith('/menu/') ? p.imagen_url : `${API_BASE}${p.imagen_url}`}
                      alt=""
                      className="h-12 w-12 shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 shrink-0 rounded-lg bg-cal" />
                  )}
                  <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-titulo font-semibold">{p.nombre}</span>
                    <span
                      className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold ${
                        CLASE_ESTADO[p.estado]
                      }`}
                    >
                      {ETIQUETA_ESTADO[p.estado]}
                    </span>
                    {p.compartido && (
                      <span
                        className="shrink-0 rounded bg-cal px-2 py-0.5 text-[11px] font-semibold text-salvia"
                        title="Este producto pertenece a otra sucursal; sólo se puede editar desde ahí."
                      >
                        Compartido
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-sm text-salvia">
                    <span className="num font-semibold text-tinta">{dinero(p.precio)}</span>
                    {p.categoria_nombre && <span>{p.categoria_nombre}</span>}
                    {p.controla_stock === 1 && (
                      <span className={bajo ? 'font-semibold text-miel-oscuro' : ''}>
                        {cantidad(p.stock)} {p.unidad.toLowerCase()}
                        {bajo && ' · por agotarse'}
                      </span>
                    )}
                    {p.codigo_barras && <span className="font-dato text-xs">{p.codigo_barras}</span>}
                  </div>
                  </div>
                </button>

                <div className="flex shrink-0 gap-1">
                  {p.compartido ? (
                    <span className="px-3 py-2 text-xs text-salvia">Edítalo en su sucursal de origen</span>
                  ) : p.estado === 'publicado' ? (
                    <button
                      onClick={() => accionRapida(p, 'agotado')}
                      className="rounded-lg px-3 py-2 text-xs font-semibold text-miel-oscuro hover:bg-miel-claro"
                      title="Quitarlo del menú hasta que vuelva a haber"
                    >
                      Agotado
                    </button>
                  ) : (
                    <button
                      onClick={() => accionRapida(p, 'publicado')}
                      className="rounded-lg px-3 py-2 text-xs font-semibold text-nopal-oscuro hover:bg-nopal-claro"
                    >
                      A la venta
                    </button>
                  )}
                  {!p.compartido && (
                    <>
                      <button
                        onClick={() => accionRapida(p, 'duplicar')}
                        className="rounded-lg px-3 py-2 text-xs font-semibold text-salvia hover:bg-cal"
                        title="Crear una copia para capturar una variante"
                      >
                        Copiar
                      </button>
                      <button
                        onClick={() => accionRapida(p, 'archivado')}
                        className="rounded-lg px-3 py-2 text-xs font-semibold text-barro hover:bg-barro-claro"
                        title="Quitarlo del catálogo. No se borra el historial, sólo deja de aparecer."
                      >
                        Quitar
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {abierto && (
        <EditorProducto
          sucursalId={sucursalId}
          sucursales={sucursales}
          producto={editando}
          categorias={categorias}
          codigoNuevo={codigoNuevo}
          tipoPorDefecto={tipo}
          alGuardar={() => {
            cerrarEditor();
            void cargar();
          }}
          alCerrar={cerrarEditor}
        />
      )}
    </div>
  );
}
